/**
 * 注释节点组件
 *
 * 用于添加注释说明，无连接点
 * 可调整大小的文本区域
 */

import React, { memo, useState, useCallback } from 'react';
import type { NodeProps } from '@xyflow/react';
import type { NoteNodeData } from '../../../types/workflow';
import { useWorkflowStore } from '../../../store/workflowStore';
import { cn } from './nodeStyles';

// 预设颜色
const noteColors = {
  default: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    text: 'text-yellow-800',
    header: 'bg-yellow-100',
    dot: 'bg-yellow-400',
  },
  blue: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-800',
    header: 'bg-blue-100',
    dot: 'bg-blue-400',
  },
  green: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-800',
    header: 'bg-green-100',
    dot: 'bg-green-400',
  },
  pink: {
    bg: 'bg-pink-50',
    border: 'border-pink-200',
    text: 'text-pgray-800',
    header: 'bg-pink-100',
    dot: 'bg-pink-400',
  },
  purple: {
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-800',
    header: 'bg-purple-100',
    dot: 'bg-purple-400',
  },
  gray: {
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    text: 'text-gray-800',
    header: 'bg-gray-100',
    dot: 'bg-gray-400',
  },
};

type NoteColor = keyof typeof noteColors;

const NoteNodeComponent: React.FC<NodeProps> = ({
  id,
  data,
  selected,
}) => {
  // 类型断言
  const nodeData = data as unknown as NoteNodeData;

  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(nodeData.content || '');

  const updateNodeForm = useWorkflowStore((state) => state.updateNodeForm);

  // 获取颜色配置
  const colorKey = (nodeData.color as NoteColor) || 'default';
  const colors = noteColors[colorKey] || noteColors.default;

  // 保存编辑内容
  const handleSave = useCallback(() => {
    updateNodeForm(id, { content: editContent });
    setIsEditing(false);
  }, [id, editContent, updateNodeForm]);

  // 取消编辑
  const handleCancel = useCallback(() => {
    setEditContent(nodeData.content || '');
    setIsEditing(false);
  }, [nodeData.content]);

  // 双击进入编辑模式
  const handleDoubleClick = useCallback(() => {
    setIsEditing(true);
  }, []);

  return (
    <div
      className={cn(
        'min-w-[160px] max-w-[300px] rounded-lg border-2 shadow-sm',
        'transition-all duration-150',
        colors.bg,
        colors.border,
        // 选中状态
        selected && 'ring-2 ring-paper-500 ring-offset-2',
        // 半透明背景
        'bg-opacity-90',
      )}
      onDoubleClick={handleDoubleClick}
      data-node-id={id}
      data-node-type="note"
    >
      {/* 节点头部 - 拖拽手柄 */}
      <div className={cn(
        'flex items-center gap-2 px-3 py-1.5 border-b',
        colors.header,
        colors.border,
        'rounded-t-md cursor-move',
      )}>
        {/* 注意图标 */}
        <svg className="w-3.5 h-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
        <span className="text-xs font-medium text-gray-500">
          {nodeData.label || '注释'}
        </span>
      </div>

      {/* 节点内容 */}
      <div className="p-3">
        {isEditing ? (
          <div className="space-y-2">
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className={cn(
                'w-full min-h-[60px] px-2 py-1.5 text-sm rounded border',
                'focus:outline-none focus:ring-2 focus:ring-agent-300',
                colors.bg,
                colors.text,
                'border-gray-200',
              )}
              placeholder="输入注释内容..."
              autoFocus
            />
            <div className="flex justify-end gap-1.5">
              <button
                onClick={handleCancel}
                className="px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 rounded"
              >
                取消
              </button>
              <button
                onClick={handleSave}
                className="px-2 py-1 text-xs text-white bg-ink-800 hover:bg-ink-700 rounded"
              >
                保存
              </button>
            </div>
          </div>
        ) : (
          <div className={cn(
            'text-sm whitespace-pre-wrap',
            colors.text,
            !nodeData.content && 'text-gray-400 italic',
          )}>
            {nodeData.content || '双击编辑注释内容'}
          </div>
        )}
      </div>

      {/* 颜色指示器（右下角） */}
      <div className={cn(
        'absolute bottom-2 right-2 w-3 h-3 rounded-full',
        colors.dot,
      )} />
    </div>
  );
};

NoteNodeComponent.displayName = 'NoteNode';

export const NoteNode = memo(NoteNodeComponent);
export default NoteNode;
